import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";
import { PAGES } from "../../routes/PAGES";
import styles from "./NavBar.module.scss";
import { getToken, isUser } from "../../services/auth";
import { useCommunity, useCurrentUser, useUserProfile } from "../../context/community";
import { usePermissions } from "../../context/permissions";
import LangService from "../../services/LangService";
import { Col } from "react-grid-system";
import { ReactComponent as FindPeopleIcon } from "assets/Imgs/find_people.svg";
import { ReactComponent as CommunityIcon } from "assets/Imgs/community.svg";
import { ReactComponent as InviteIcon } from "assets/Imgs/invite.svg";
import { ReactComponent as CalendarIcon } from "assets/Imgs/calendar.svg";
import { ReactComponent as CareerIcon } from "assets/Imgs/company.svg";
import { ReactComponent as MentorshipIcon } from "assets/Imgs/handshake.svg";
import { ReactComponent as MatchesIcon } from "assets/Imgs/matches.svg";
import { ReactComponent as RequestIcon } from "assets/Imgs/requests.svg";
import { ReactComponent as AnalyticsIcon } from "assets/Imgs/assessment.svg";
import { ReactComponent as SettingsIcon } from 'assets/Imgs/settings.svg';
import { ReactComponent as ChatIcon } from 'assets/Imgs/chat.svg';
import { ReactComponent as InfoIcon } from 'assets/Imgs/info.svg';
import { ReactComponent as AdminIcon } from 'assets/Imgs/settings.svg'; // Using settings icon as admin icon for now
import { useVTBOnboarding } from '../VTBOnboarding/useVTBOnboarding';
import { ChatNavBadge } from 'features/chat/components/ChatNavBadge';


const SHOULD_BE_HIDDEN = [PAGES.QUESTIONS, PAGES.ONBOARDING, PAGES.LAST_STEP, PAGES.CONFIRMED];
const MODERATION_STATUS_EVENT_ONLY = 'EVENT_ONLY';

function resolveCareerMenuText() {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('local') : null;
    const stored = raw ? String(raw).trim().toUpperCase() : '';
    const current = String(LangService.lang || '').trim().toUpperCase();
    const langKey = current.startsWith('RU')
        ? 'RU'
        : current.startsWith('EN')
            ? 'EN'
            : (stored.startsWith('RU') ? 'RU' : (stored.startsWith('EN') ? 'EN' : 'EN'));
    return langKey === 'RU' ? 'Карьера' : 'Career';
}

function resolveMentorshipMenuText() {
    const current = String(LangService.lang || '').trim().toUpperCase();
    return current.startsWith('RU') ? 'Менторинг' : 'Mentorship';
}

function navPath(to) {
    return String(to || '').split('?')[0].replace(/\/$/, '') || '/';
}

function isChatNavPath(to) {
    const path = navPath(to);
    if (path === PAGES.ADMIN_CHATS || path.startsWith(`${PAGES.ADMIN_CHATS}/`)) {
        return false;
    }
    return path === PAGES.CHATS || path === PAGES.MANAGE_CHATS || path.endsWith('/chats') || path.includes('/manage-chats');
}

function dedupeNavLinks(links) {
    const seen = new Set();
    return links.filter((link) => {
        const key = navPath(link.to);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

function hasCommunityOption(community, optionName) {
    return Array.isArray(community?.options) && community.options.some(option => {
        const optionType = typeof option === 'string' ? option : option?.optionType;
        return optionType === optionName;
    });
}

function isCareerRoute(path) {
    const normalizedPath = String(path || '').toLowerCase();
    return normalizedPath === PAGES.VACANCY_LIST
        || normalizedPath.startsWith('/job')
        || normalizedPath.startsWith('/jobs')
        || normalizedPath.startsWith('/vacancy')
        || normalizedPath.startsWith('/vacancies')
        || normalizedPath.startsWith('/vtb/career')
        || normalizedPath === '/career'
        || normalizedPath.startsWith('/career/');
}

function pathOrChild(pathname, base) {
    return pathname === base || pathname.startsWith(`${base}/`);
}

export function makeLinkIsActive(link, slug) {
    const to = link?.to;
    if (to === `/u/${slug}`) {
        return (pathname) => pathname === to || pathname === PAGES.PROFILE;
    }
    if (to === PAGES.EVENT_LIST) {
        return (pathname) => pathname === PAGES.EVENT_LIST
            || (pathname.startsWith('/event/') && pathname !== '/event/create');
    }
    if (to === PAGES.VACANCY_LIST) {
        return (pathname) => pathname === PAGES.VACANCY_LIST
            || pathname.startsWith('/job/')
            || pathname.startsWith('/jobs/')
            || pathname.startsWith('/vacancy/')
            || pathname.startsWith('/vacancies/');
    }
    if (to === PAGES.CONTACTS || to === PAGES.CHATS || to === PAGES.ADMIN) {
        return (pathname) => pathOrChild(pathname, to);
    }
    return (pathname) => pathname === to;
}

export function useNavbarLinks() {
    const history = useHistory();
    const { pathname } = useLocation();
    const { connectedParticipants, enabled, slug, moderationStatus, onboardingResume } = useCurrentUser();
    const userProfile = useUserProfile();
    const { community, navigationConfig } = useCommunity();
    const { isAdmin, canModerateCommunity, loading: permissionsLoading } = usePermissions();
    const user = isUser();
    const token = getToken();
    const pagesMap = LangService.map.pages;
    const currentLang = String(LangService.lang || 'EN').toUpperCase();
    const careerMenuText = resolveCareerMenuText();
    const mentorshipMenuText = resolveMentorshipMenuText();

    // VTB Onboarding hook for Help button - always call hook
    const vtbOnboarding = useVTBOnboarding();
    const isVTBCommunity = community?.domain === 'vtb';

    const getCustomNavigationLinks = () => {
        const navLinks = [];

        if (!navigationConfig?.customNavigation) {
            return null; // Use default navigation
        }

        const { tabs, customTabs = [] } = navigationConfig;

        const resolveLabel = (tabKey, configuredLabel, defaultText) => {
            // Same behavior as events: built-in career tab should use localized default text.
            if (tabKey === 'career') {
                return defaultText;
            }
            if (configuredLabel && typeof configuredLabel === 'object') {
                return configuredLabel[currentLang]
                    || configuredLabel[currentLang.toLowerCase()]
                    || defaultText;
            }
            if (typeof configuredLabel === 'string' && configuredLabel.trim()) {
                const normalizedLabel = configuredLabel.trim().toLowerCase();
                // Keep localized default when config still has legacy EN-only label.
                if (normalizedLabel === 'career' && defaultText && defaultText !== 'Career') {
                    return defaultText;
                }
                return configuredLabel;
            }
            return defaultText;
        };

        // Helper to get default tab data
        const getDefaultTabData = (tabKey) => {
            const defaultTabs = {
                myCommunities: {
                    to: PAGES.MY_COMMUNITIES,
                    defaultText: pagesMap.MyCommunities.menuText,
                    icon: <CommunityIcon />
                },
                participants: {
                    to: PAGES.PARTICIPANTS,
                    defaultText: pagesMap.Participants.menuText,
                    icon: <FindPeopleIcon />
                },
                contacts: {
                    to: PAGES.CONTACTS,
                    defaultText: pagesMap.Contacts.menuText,
                    icon: <MatchesIcon />
                },
                events: {
                    to: PAGES.EVENT_LIST,
                    defaultText: pagesMap.EventList.menuText,
                    icon: <CalendarIcon />
                },
                career: {
                    to: PAGES.VACANCY_LIST,
                    defaultText: pagesMap.VacancyList?.menuText || 'Career',
                    icon: <CalendarIcon /> // Using CalendarIcon for now, can be replaced later
                },
                chats: {
                    to: PAGES.CHATS,
                    defaultText: pagesMap.Chats?.menuText || 'Chats',
                    icon: <ChatIcon />,
                    badge: <ChatNavBadge />
                },
                admin: {
                    to: PAGES.ADMIN,
                    defaultText: pagesMap.UserAdmin.title,
                    icon: <AdminIcon />
                }
            };
            return defaultTabs[tabKey];
        };

        // Add standard tabs based on configuration
        Object.entries(tabs || {}).forEach(([tabKey, tabConfig]) => {
            if (tabConfig.enabled !== false) {
                const defaultData = getDefaultTabData(tabKey);
                if (defaultData) {
                    // Skip participants if not transparent community (unless explicitly enabled)
                    if (tabKey === 'participants' && !community.transparent && !tabConfig.forceEnabled) {
                        return;
                    }

                    if (tabKey === 'profile') {
                        return;
                    }

                    navLinks.push({
                        ...defaultData,
                        text: resolveLabel(tabKey, tabConfig.label, defaultData.defaultText),
                        order: tabConfig.order || 999
                    });
                }
            }
        });

        // Add custom tabs
        customTabs.forEach(customTab => {
            if (customTab.url === PAGES.PROFILE || customTab.url === `/u/${slug}`) {
                return;
            }
            if (customTab.id && customTab.label && customTab.url && !isChatNavPath(customTab.url)) {
                const customLabel = (typeof customTab.label === 'object')
                    ? (customTab.label[currentLang]
                        || customTab.label[currentLang.toLowerCase()]
                        || customTab.label.EN
                        || customTab.label.en
                        || customTab.id)
                    : customTab.label;
                const normalizedCustomId = String(customTab.id || '').toLowerCase();
                const normalizedCustomLabel = String(customLabel || '').trim().toLowerCase();
                navLinks.push({
                    to: customTab.url,
                    text: (
                        isCareerRoute(customTab.url)
                        || normalizedCustomId.includes('career')
                        || normalizedCustomLabel === 'career'
                    ) ? careerMenuText : customLabel,
                    icon: <CommunityIcon />, // Default icon for custom tabs
                    order: customTab.order || 999,
                    external: customTab.external || false
                });
            }
        });

        if (hasCommunityOption(community, 'ENABLE_MENTORSHIP')
            && !navLinks.some(link => link.to === PAGES.MENTORSHIP)) {
            navLinks.push({
                to: PAGES.MENTORSHIP,
                text: mentorshipMenuText,
                icon: <MentorshipIcon />,
                order: 999,
            });
        }

        if (!navLinks.some(link => link.to === PAGES.CHATS)) {
            navLinks.push({
                to: PAGES.CHATS,
                text: pagesMap.Chats?.menuText || 'Chats',
                icon: <ChatIcon />,
                badge: <ChatNavBadge />,
                order: 999,
            });
        }

        // Sort by order
        navLinks.sort((a, b) => a.order - b.order);

        return dedupeNavLinks(navLinks);
    };

    // Incomplete profile stays on the question flow. V2 start() creates a draft
    // participant with profileFilled=false before the survey is done.
    useEffect(() => {
        if (!user || enabled !== false) return;
        if (pathname === PAGES.PROFILE || SHOULD_BE_HIDDEN.includes(pathname)) return;
        if (onboardingResume === 'PROFILE') {
            history.replace(PAGES.ONBOARDING);
            return;
        }
        if (onboardingResume === 'QUESTIONS') {
            history.replace(PAGES.QUESTIONS);
            return;
        }
        history.replace(PAGES.PROFILE);
    }, [user, enabled, history, pathname, onboardingResume]);

    const navbarLinks = useMemo(() => {
        const navbarLinks = [];

        if (user) {
            if (enabled === false) {
                return navbarLinks;
            }

            const isEventOnlyParticipant = moderationStatus === MODERATION_STATUS_EVENT_ONLY || userProfile?.moderationStatus === MODERATION_STATUS_EVENT_ONLY;
            const eventOnlyAllowedPaths = [PAGES.EVENT_LIST, PAGES.FAQ, "/u/" + slug];

            if (isEventOnlyParticipant) {
                // EVENT_ONLY: show all menu items; restricted ones open onboarding modal on click
                const addEventOnly = (link) => navbarLinks.push({
                    ...link,
                    restrictedForEventOnly: !eventOnlyAllowedPaths.includes(link.to),
                });
                addEventOnly({ to: PAGES.MY_COMMUNITIES, text: pagesMap.MyCommunities.menuText, icon: <CommunityIcon /> });
                community.transparent && addEventOnly({ to: PAGES.PARTICIPANTS, text: pagesMap.Participants.menuText, icon: <FindPeopleIcon /> });
                addEventOnly({ to: PAGES.CONTACTS, text: pagesMap.Contacts.menuText, icon: <MatchesIcon /> });
                addEventOnly({ to: PAGES.EVENT_LIST, text: pagesMap.EventList.menuText, icon: <CalendarIcon /> });
                addEventOnly({ to: PAGES.CHATS, text: pagesMap.Chats?.menuText || 'Chats', icon: <ChatIcon />, badge: <ChatNavBadge /> });
                const hasFaqOption = Array.isArray(community?.options) && (
                    community.options.includes('ENABLE_FAQ') || community.options.includes('ENABLE_FAQ_AVITO')
                );
                if (hasFaqOption) {
                    addEventOnly({ to: PAGES.FAQ, text: pagesMap.FAQ?.menuText || 'FAQ', icon: <InfoIcon /> });
                }
                // Admin not shown for EVENT_ONLY (option-based, not just permissions)
                return navbarLinks;
            }

            // Check if custom navigation is configured
            const customNavLinks = getCustomNavigationLinks();
            if (customNavLinks) {
                return customNavLinks;
            }

            // Default navigation (existing code)
            navbarLinks.push(
                {
                    to: PAGES.MY_COMMUNITIES,
                    text: pagesMap.MyCommunities.menuText,
                    icon: <CommunityIcon />
                })
            community.transparent && navbarLinks.push(
                {
                    to: PAGES.PARTICIPANTS,
                    text: pagesMap.Participants.menuText,
                    icon: <FindPeopleIcon />
                },
            )
            navbarLinks.push(
                {
                    to: PAGES.CONTACTS,
                    text: pagesMap.Contacts.menuText,
                    icon: <MatchesIcon />
                },
            )
            navbarLinks.push(
                {
                    to: PAGES.EVENT_LIST,
                    text: pagesMap.EventList.menuText,
                    icon: <CalendarIcon />
                },
            )
            // Add Career only if community option ENABLE_CAREER_TAB is present
            if (Array.isArray(community?.options) && community.options.some(opt => {
                const optionType = typeof opt === 'string' ? opt : (opt?.optionType || opt);
                return optionType === 'ENABLE_CAREER_TAB';
            })) {
                navbarLinks.push({
                    to: PAGES.VACANCY_LIST,
                    text: careerMenuText,
                    icon: <CareerIcon />
                });
            }
            if (hasCommunityOption(community, 'ENABLE_MENTORSHIP')) {
                navbarLinks.push({
                    to: PAGES.MENTORSHIP,
                    text: mentorshipMenuText,
                    icon: <MentorshipIcon />
                });
            }
            navbarLinks.push({
                to: PAGES.CHATS,
                text: pagesMap.Chats?.menuText || 'Chats',
                icon: <ChatIcon />,
                badge: <ChatNavBadge />
            });
            // Conditionally add FAQ before Admin if community has ENABLE_FAQ or legacy ENABLE_FAQ_AVITO
            const hasFaqOption = Array.isArray(community?.options) && (
                community.options.includes('ENABLE_FAQ') || community.options.includes('ENABLE_FAQ_AVITO')
            );
            if (hasFaqOption) {
                navbarLinks.push({
                    to: PAGES.FAQ,
                    text: pagesMap.FAQ?.menuText || 'FAQ',
                    icon: <InfoIcon />,
                });
            }

            // Add Admin menu for users with admin permissions
            if (!permissionsLoading && isAdmin()) {
                navbarLinks.push({
                    to: PAGES.ADMIN,
                    text: pagesMap.UserAdmin.title,
                    icon: <AdminIcon />
                });
            }
        } else if (!permissionsLoading && isAdmin()) {
            navbarLinks.push(
                {
                    to: PAGES.EVENT,
                    text: pagesMap.Event.menuText,
                    icon: <CommunityIcon />
                },
                {
                    to: PAGES.DASHBOARD,
                    text: pagesMap.Admin.analytics,
                    icon: <AnalyticsIcon />
                },
                {
                    to: PAGES.NOTIFICATIONS,
                    text: pagesMap.Notifications.menuText,
                    icon: <RequestIcon />
                },
            )

            // if (width > 768) {
            //     navbarLinks.push(
            //         {
            //             to: PAGES.SETTINGS,
            //             text: pagesMap.AdminSettings.menuText,
            //             icon: <SettingsIcon />
            //         },
            //     )
            // }
        }

        // Add Help button for VTB community users
        if (user && isVTBCommunity) {
            navbarLinks.push({
                to: '#',
                text: 'Помощь',
                icon: <InfoIcon />,
                onClick: () => vtbOnboarding.startOnboarding(),
                isHelp: true
            });
        }

        return dedupeNavLinks(navbarLinks).map((link) => {
            const remapped = ((isCareerRoute(link?.to) || String(link?.text || '').trim().toLowerCase() === 'career') && !link?.preserveCareerLabel)
                ? {
                    ...link,
                    text: careerMenuText || link.text || 'Career',
                }
                : link;
            return {
                ...remapped,
                isActive: makeLinkIsActive(remapped, slug),
            };
        });
    }, [community, user, isAdmin, enabled, LangService.lang, slug, navigationConfig, isVTBCommunity, vtbOnboarding, permissionsLoading, moderationStatus, userProfile?.moderationStatus, careerMenuText, mentorshipMenuText]);

    const [showOnboardingModal, setShowOnboardingModal] = useState(false);

    const visible = Boolean(
        token
        && !(user && !connectedParticipants?.length)
        && !SHOULD_BE_HIDDEN.includes(history.location.pathname)
    );

    return {
        links: visible ? navbarLinks : [],
        visible,
        showOnboardingModal,
        setShowOnboardingModal,
        slug,
    };
}

const NavBar = () => {
    const history = useHistory();
    const {
        links: navbarLinks,
        visible,
        showOnboardingModal,
        setShowOnboardingModal,
        slug,
    } = useNavbarLinks();

    if (!visible) {
        return null;
    }
    return (
        <>
            {
                navbarLinks.map(({ to, text, icon, onClick, isHelp, restrictedForEventOnly, badge }) => (
                    <Col xs="content" key={to}>
                        {isHelp ? (
                            <button
                                onClick={onClick}
                                className={styles.navButton}
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                {icon}
                                {text}
                                {badge}
                            </button>
                        ) : restrictedForEventOnly ? (
                            <button
                                type="button"
                                onClick={() => setShowOnboardingModal(true)}
                                className={styles.navButton}
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                {icon}
                                {text}
                                {badge}
                            </button>
                        ) : (
                            <NavLink
                                to={to}
                                isActive={(_, location) => makeLinkIsActive({ to }, slug)(location.pathname)}
                                onClick={() => {
                                    gtag('event', `navbar_to_${to.replace('/', '')}`, {
                                        from: history.location.pathname,
                                        to
                                    })
                                }}
                                className={styles.navButton}
                                activeClassName={styles.navActiveButton}
                            >
                                {icon}
                                {text}
                                {badge}
                            </NavLink>
                        )}
                    </Col>
                ))
            }
            {showOnboardingModal && (
                <EventOnlyOnboardingModal slug={slug} onClose={() => setShowOnboardingModal(false)} />
            )}
        </>
    );
};

export const EventOnlyOnboardingModal = ({ slug, onClose }) => {
    const history = useHistory();
    const eventOnlyModal = LangService.map.pages?.EventOnlyOnboardingModal;
    const eventOnlyModalMessage = eventOnlyModal?.message ?? 'To view contacts, get recommendations and network you need to complete onboarding. Until then you have access to:';
    const eventOnlyModalButton = eventOnlyModal?.button ?? 'Go to questions';
    const eventOnlyEventsLink = eventOnlyModal?.eventsLink ?? 'Events';
    const eventOnlyFaqLink = eventOnlyModal?.faqLink ?? 'FAQ';
    const eventOnlyProfileLink = eventOnlyModal?.profileLink ?? 'Profile';

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="mx-4 max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <p className="mb-2 text-gray-800">{eventOnlyModalMessage}</p>
                <p className="mb-4 flex flex-wrap items-center gap-x-1 gap-y-1 text-gray-800">
                    <Link to={PAGES.EVENT_LIST} onClick={onClose} className="text-blue-600 underline hover:text-blue-800">{eventOnlyEventsLink}</Link>
                    <span>, </span>
                    <Link to={PAGES.FAQ} onClick={onClose} className="text-blue-600 underline hover:text-blue-800">{eventOnlyFaqLink}</Link>
                    <span>, </span>
                    <Link to={"/u/" + slug} onClick={onClose} className="text-blue-600 underline hover:text-blue-800">{eventOnlyProfileLink}</Link>
                </p>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50"
                    >
                        {LangService.map.common?.cancel ?? 'Cancel'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onClose();
                            history.push(PAGES.QUESTIONS, { eventOnlyCompleteQuestions: true });
                        }}
                        className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                        {eventOnlyModalButton}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NavBar;
